import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@finn/design-system/atoms/select';
import {
  FilterKey,
  FiltersResponse,
  trackFilterOptionClicked,
  useTrackFilterCategoryClicked,
} from '@finn/ua-vehicle';
import { useIntl } from '@finn/ui-utils';
import { useMemo } from 'react';

import { FiltersState } from '../../../filters-management';
import { FilterAccordion } from '../atoms/FilterAccordion';
import { FilterOptionClickFunction } from '../grouped-filters/FiltersGroup';

type Props = {
  selectedFilters: FiltersState;
  availableFilters: FiltersResponse;
  onOptionClick: FilterOptionClickFunction;
  expanded: boolean;
};

export const BrandModelFilter = ({
  selectedFilters,
  availableFilters,
  onOptionClick,
  expanded,
}: Props) => {
  const i18n = useIntl();

  const brandPlaceholder = i18n.formatMessage('general.heroFilters.allBrands');
  const modelPlaceholder = i18n.formatMessage('general.heroFilters.allModels');

  const selectedBrand = useMemo(
    () => selectedFilters.brands,
    [selectedFilters.brands]
  )[0];

  const selectedModel = useMemo(
    () => selectedFilters.models,
    [selectedFilters.models]
  )[0];

  const handleChange = (filterKey: FilterKey, value: string) => {
    onOptionClick({ [filterKey]: [value] });
    trackFilterOptionClicked(filterKey, value);
  };

  return (
    <FilterAccordion title="Marke und Modell" expanded={expanded}>
      <div className="flex flex-col gap-2">
        <Select
          value={selectedBrand ?? brandPlaceholder}
          onValueChange={(value) => handleChange(FilterKey.BRANDS, value)}
          onOpenChange={useTrackFilterCategoryClicked(FilterKey.BRANDS)}
        >
          <SelectTrigger wrapperClassName="w-full" className="w-full">
            <SelectValue
              placeholder={brandPlaceholder}
              className="body-14-regular"
            />
          </SelectTrigger>
          <SelectContent className="max-h-[340px]" withScrollDownButton>
            <SelectItem className="body-14-regular" value={brandPlaceholder}>
              <span>{brandPlaceholder}</span>
            </SelectItem>
            {availableFilters.brands
              .filter((brandItem) => brandItem.available === true)
              .map((option) => (
                <SelectItem
                  className="body-14-regular"
                  key={option.id}
                  value={option.id}
                >
                  <span>{option.id}</span>
                </SelectItem>
              ))}
          </SelectContent>
        </Select>

        <Select
          value={selectedModel ?? modelPlaceholder}
          onValueChange={(value) => handleChange(FilterKey.MODELS, value)}
          onOpenChange={useTrackFilterCategoryClicked(FilterKey.MODELS)}
          disabled={!selectedBrand}
        >
          <SelectTrigger wrapperClassName="w-full" className="w-full">
            <SelectValue
              placeholder={modelPlaceholder}
              className="body-14-regular"
            />
          </SelectTrigger>
          <SelectContent className="max-h-[340px]" withScrollDownButton>
            <SelectItem className="body-14-regular" value={modelPlaceholder}>
              <span>{modelPlaceholder}</span>
            </SelectItem>
            {availableFilters.brands
              .find(
                (brandItem) =>
                  brandItem.id === (selectedBrand ?? brandPlaceholder)
              )
              ?.models.filter((modelItem) => modelItem.available === true)
              .map((option) => (
                <SelectItem
                  className="body-14-regular"
                  key={option.id}
                  value={option.id}
                >
                  <span>{option.id}</span>
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>
    </FilterAccordion>
  );
};
